import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Check,
  ExternalLink,
  Loader2,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { DEMO_IDS, getDemoCompetitorVideos } from "@/mock/demo";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConvexAction, useConvexMutation } from "@convex-dev/react-query";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Id } from "@cvx/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VideoEmbed } from "@/components/ui/video-embed";
import { api } from "@cvx/_generated/api";
import { useAccount } from "@/state/useAccount";
import { useQuery as useConvexQuery } from "convex/react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";

type PlatformOption = "tiktok" | "instagram" | "youtube-shorts";

interface CompetitorVideo {
  id: string;
  url: string;
  description: string;
  platform: PlatformOption;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  userHandle: string;
  hashtags: string[];
}

export default function CompetitorAnalysis() {
  const { appId, demoModeEnabled } = useAccount();

  // Check if we're in demo mode (founder demo only)
  const isDemo =
    demoModeEnabled &&
    (appId as unknown as string) === (DEMO_IDS.appId as unknown as string);
  const { toast } = useToast();
  const [userHandle, setUserHandle] = useState("");
  const [platform, setPlatform] = useState<PlatformOption>("tiktok");
  const [videos, setVideos] = useState<CompetitorVideo[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedVideos, setSelectedVideos] = useState<Set<string>>(new Set());
  const [saveToCampaignDialogOpen, setSaveToCampaignDialogOpen] =
    useState(false);
  const [selectedCampaigns, setSelectedCampaigns] = useState<Set<string>>(
    new Set(),
  );

  // Fetch campaigns for this app
  const campaigns = useConvexQuery(api.campaigns.getAllCampaigns, {
    appId: appId as Id<"apps">,
  });

  // Fetch saved competitor videos
  const savedVideos = useConvexQuery(
    api.competitors.getSavedCompetitorVideoUrls,
    appId && !isDemo ? { appId: appId as Id<"apps"> } : "skip",
  );

  // Fetch all saved competitor videos with campaign grouping
  const allSavedVideos = useConvexQuery(
    api.competitors.getCompetitorVideos,
    appId && !isDemo ? { appId: appId as Id<"apps"> } : "skip",
  );

  // Use demo data when in demo mode
  const effectiveAllSavedVideos = isDemo
    ? getDemoCompetitorVideos()
    : allSavedVideos;

  // Actions
  const retrieveTTProfileVideos = useConvexAction(
    api.socials.retrieveTTProfileVideos,
  );
  const retrieveIGProfileVideos = useConvexAction(
    api.socials.retrieveInstagramProfileVideosAction,
  );
  const retrieveYTShortsProfileVideos = useConvexAction(
    api.socials.retrieveYTShortsProfileVideos,
  );
  const { mutateAsync: addCompetitorVideosAction } = useMutation({
    mutationFn: useConvexMutation(api.competitors.addCompetitorVideos),
  });
  const { mutateAsync: removeCompetitorVideosAction } = useMutation({
    mutationFn: useConvexMutation(api.competitors.removeCompetitorVideos),
  });

  // Using shared VideoEmbed component for all platforms

  const handleSearch = async () => {
    if (!userHandle.trim()) {
      toast({
        title: "Competitor handle required",
        description:
          "Please enter a competitor's handle to search for their videos",
        variant: "destructive",
      });
      return;
    }

    setIsSearching(true);
    try {
      let transformedVideos: CompetitorVideo[] = [];

      if (platform === "tiktok") {
        const response = await retrieveTTProfileVideos({ handle: userHandle });
        transformedVideos =
          response?.map(
            (video: {
              id: string;
              webVideoUrl: string;
              text?: string;
              playCount?: number;
              diggCount?: number;
              commentCount?: number;
              shareCount?: number;
              authorMeta: { name: string };
              hashtags?: { name: string }[];
            }) => ({
              id: video.id,
              url: video.webVideoUrl,
              description: video.text || "No description",
              platform: "tiktok" as const,
              views: video.playCount || 0,
              likes: video.diggCount || 0,
              comments: video.commentCount || 0,
              shares: video.shareCount || 0,
              userHandle: video.authorMeta.name,
              hashtags: video.hashtags?.map((tag) => tag.name) || [],
            }),
          ) || [];
      } else if (platform === "instagram") {
        const response = await retrieveIGProfileVideos({ handle: userHandle });
        transformedVideos =
          response?.map(
            (video: {
              id: string;
              url: string;
              caption?: string;
              likesCount?: number;
              commentsCount?: number;
              videoPlayCount?: number;
              ownerUsername?: string;
              hashtags?: string[];
            }) => ({
              id: video.id,
              url: video.url,
              description: video.caption || "No description",
              platform: "instagram" as const,
              views: video.videoPlayCount || 0,
              likes: video.likesCount || 0,
              comments: video.commentsCount || 0,
              shares: 0,
              userHandle: video.ownerUsername || userHandle,
              hashtags: video.hashtags || [],
            }),
          ) || [];
      } else if (platform === "youtube-shorts") {
        const response = await retrieveYTShortsProfileVideos({
          handle: userHandle,
        });
        transformedVideos =
          response?.map(
            (video: {
              id: string;
              url: string;
              title?: string;
              description?: string;
              statistics?: {
                viewCount?: number;
                likeCount?: number;
                commentCount?: number;
              };
            }) => ({
              id: video.id,
              url: video.url,
              description: video.title || video.description || "No description",
              platform: "youtube-shorts" as const,
              views: video.statistics?.viewCount || 0,
              likes: video.statistics?.likeCount || 0,
              comments: video.statistics?.commentCount || 0,
              shares: 0,
              userHandle: userHandle,
              hashtags: [],
            }),
          ) || [];
      }

      setVideos(transformedVideos);
      setSelectedVideos(new Set()); // Reset selection

      toast({
        title: "Search completed",
        description: `Found ${transformedVideos.length} videos from @${userHandle}`,
      });
    } catch (error) {
      console.error("Search error:", error);
      toast({
        title: "Search failed",
        description:
          "There was an error searching for videos. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleVideoSelection = (videoId: string, checked: boolean) => {
    const newSelected = new Set(selectedVideos);
    if (checked) {
      newSelected.add(videoId);
    } else {
      newSelected.delete(videoId);
    }
    setSelectedVideos(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedVideos(new Set(videos.map((v) => v.id)));
    } else {
      setSelectedVideos(new Set());
    }
  };

  const handleCampaignSelection = (campaignId: string, checked: boolean) => {
    const newSelected = new Set(selectedCampaigns);
    if (checked) {
      newSelected.add(campaignId);
    } else {
      newSelected.delete(campaignId);
    }
    setSelectedCampaigns(newSelected);
  };

  const handleSaveToSampaigns = async () => {
    if (selectedVideos.size === 0) {
      toast({
        title: "No videos selected",
        description: "Please select at least one video to save",
        variant: "destructive",
      });
      return;
    }

    if (selectedCampaigns.size === 0) {
      toast({
        title: "No campaigns selected",
        description: "Please select at least one campaign to save videos to",
        variant: "destructive",
      });
      return;
    }

    try {
      const selectedVideoData = videos.filter((v) => selectedVideos.has(v.id));
      const campaignIds = Array.from(selectedCampaigns) as Id<"campaigns">[];

      await addCompetitorVideosAction({
        appId: appId as Id<"apps">,
        campaignIds,
        videos: selectedVideoData.map((v) => ({
          videoUrl: v.url,
          title: v.description || `Video by @${v.userHandle}`,
          platform: v.platform,
          views: v.views,
          likes: v.likes,
          comments: v.comments,
          shares: v.shares,
          userHandle: v.userHandle,
        })),
      });

      toast({
        title: "Videos saved successfully",
        description: `Saved ${selectedVideos.size} videos to ${selectedCampaigns.size} campaign(s)`,
      });

      // Reset selections and close dialog
      setSelectedVideos(new Set());
      setSelectedCampaigns(new Set());
      setSaveToCampaignDialogOpen(false);
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Save failed",
        description: "There was an error saving the videos. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveFromCampaigns = async (videoUrl: string) => {
    try {
      await removeCompetitorVideosAction({
        appId: appId as Id<"apps">,
        videoUrls: [videoUrl],
      });

      toast({
        title: "Video removed",
        description: "Video removed from all campaigns successfully",
      });
    } catch (error) {
      console.error("Remove error:", error);
      toast({
        title: "Remove failed",
        description: "There was an error removing the video. Please try again.",
        variant: "destructive",
      });
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const getPlatformColor = (platform: string) => {
    if (platform === "tiktok") return "bg-pink-500";
    if (platform === "instagram")
      return "bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-500";
    return "bg-red-600"; // youtube-shorts
  };

  const isVideoSaved = (videoUrl: string) => {
    return savedVideos && savedVideos[videoUrl];
  };

  const getSavedCampaignNames = (videoUrl: string) => {
    if (!savedVideos || !savedVideos[videoUrl] || !campaigns) return [];

    const campaignIds = savedVideos[videoUrl].campaignIds;
    return campaigns
      .filter((campaign) => campaignIds.includes(campaign._id))
      .map((campaign) => campaign.name);
  };

  // Group saved videos by campaign
  const groupVideosByCampaign = () => {
    if (!effectiveAllSavedVideos || !campaigns) return {};

    const groupedVideos: Record<
      string,
      {
        campaign: NonNullable<typeof campaigns>[0];
        videos: any[];
      }
    > = {};

    // Initialize groups for each campaign
    campaigns.forEach((campaign) => {
      groupedVideos[campaign._id] = {
        campaign,
        videos: [],
      };
    });

    // Group videos by their campaigns
    if (Array.isArray(effectiveAllSavedVideos)) {
      effectiveAllSavedVideos.forEach((video) => {
        video.campaignIds.forEach((campaignId) => {
          if (groupedVideos[campaignId]) {
            groupedVideos[campaignId].videos.push(video);
          }
        });
      });
    }

    // Remove empty campaigns
    return Object.fromEntries(
      Object.entries(groupedVideos).filter(
        ([_, data]) => data.videos.length > 0,
      ),
    );
  };

  const groupedVideos = groupVideosByCampaign();

  return (
    <div className="space-y-6">
      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search Competitor Videos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Platform *</Label>
              <Select
                value={platform}
                onValueChange={(v) => setPlatform(v as PlatformOption)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tiktok">TikTok</SelectItem>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="youtube-shorts">YouTube Shorts</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="userHandle">
                {platform === "tiktok"
                  ? "TikTok Handle *"
                  : platform === "instagram"
                    ? "Instagram Handle *"
                    : "YouTube Handle *"}
              </Label>
              <Input
                id="userHandle"
                placeholder={
                  platform === "tiktok"
                    ? "Enter TikTok handle (without @)"
                    : platform === "instagram"
                      ? "Enter Instagram handle (without @)"
                      : "Enter YouTube handle (without @)"
                }
                value={userHandle}
                onChange={(e) => setUserHandle(e.target.value)}
              />
            </div>
          </div>

          <Button
            onClick={handleSearch}
            disabled={isSearching}
            className="w-full"
          >
            {isSearching ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                Search Videos
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Saved Videos by Campaign - Only show when not searching */}
      {videos.length === 0 &&
        !isSearching &&
        Object.keys(groupedVideos).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Saved Competitor Videos</CardTitle>
              <p className="text-sm text-muted-foreground">
                Videos saved to your campaigns that hired creators will see as
                inspiration for what to create
              </p>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" className="w-full">
                {Object.entries(groupedVideos).map(
                  ([campaignId, { campaign, videos: campaignVideos }]) => (
                    <AccordionItem key={campaignId} value={campaignId}>
                      <AccordionTrigger className="text-left">
                        <div className="flex items-center justify-between w-full mr-2">
                          <span className="font-medium">{campaign.name}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">
                              {campaignVideos.length} video
                              {campaignVideos.length === 1 ? "" : "s"}
                            </Badge>
                            <Badge
                              variant={
                                campaign.status === "active"
                                  ? "default"
                                  : "secondary"
                              }
                            >
                              {campaign.status}
                            </Badge>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                          {campaignVideos.map((video) => (
                            <Card key={video._id} className="overflow-hidden">
                              <div className="p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <Badge className="bg-pink-500 text-white">
                                    TikTok
                                  </Badge>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        handleRemoveFromCampaigns(
                                          video.videoUrl,
                                        )
                                      }
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        window.open(video.videoUrl, "_blank")
                                      }
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>

                                <VideoEmbed
                                  url={video.videoUrl}
                                  platform="tiktok"
                                  className="mb-4"
                                />

                                <div className="text-xs text-muted-foreground">
                                  Added{" "}
                                  {new Date(
                                    video._creationTime,
                                  ).toLocaleDateString()}
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ),
                )}
              </Accordion>
            </CardContent>
          </Card>
        )}

      {/* Results */}
      {videos.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Found {videos.length} Videos</CardTitle>
              <div className="flex items-center gap-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="selectAll"
                    checked={selectedVideos.size === videos.length}
                    onCheckedChange={handleSelectAll}
                  />
                  <Label htmlFor="selectAll" className="text-sm">
                    Select All ({selectedVideos.size})
                  </Label>
                </div>
                <Dialog
                  open={saveToCampaignDialogOpen}
                  onOpenChange={setSaveToCampaignDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button disabled={selectedVideos.size === 0}>
                      <Plus className="mr-2 h-4 w-4" />
                      Save to Campaigns ({selectedVideos.size})
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Save Videos to Campaigns</DialogTitle>
                      <DialogDescription>
                        Select which campaigns to save these{" "}
                        {selectedVideos.size} videos to
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      {campaigns?.map((campaign) => (
                        <div
                          key={campaign._id}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            id={campaign._id}
                            checked={selectedCampaigns.has(campaign._id)}
                            onCheckedChange={(checked) =>
                              handleCampaignSelection(
                                campaign._id,
                                checked as boolean,
                              )
                            }
                          />
                          <Label htmlFor={campaign._id} className="flex-1">
                            {campaign.name}
                          </Label>
                          <Badge
                            variant={
                              campaign.status === "active"
                                ? "default"
                                : "secondary"
                            }
                          >
                            {campaign.status}
                          </Badge>
                        </div>
                      ))}
                      <Button
                        onClick={handleSaveToSampaigns}
                        className="w-full"
                        disabled={selectedCampaigns.size === 0}
                      >
                        Save to {selectedCampaigns.size} Campaign(s)
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {videos.map((video) => {
                const isSaved = isVideoSaved(video.url);
                const savedCampaignNames = getSavedCampaignNames(video.url);

                return (
                  <Card
                    key={video.id}
                    className={`overflow-hidden ${isSaved ? "ring-2 ring-green-500" : ""}`}
                  >
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={selectedVideos.has(video.id)}
                            onCheckedChange={(checked) =>
                              handleVideoSelection(video.id, checked as boolean)
                            }
                          />
                          <span className="text-sm font-medium">
                            @{video.userHandle}
                          </span>
                          {isSaved && (
                            <Badge
                              variant="secondary"
                              className="bg-green-100 text-green-800"
                            >
                              <Check className="h-3 w-3 mr-1" />
                              Saved
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            className={`${getPlatformColor(video.platform)} text-white`}
                          >
                            {video.platform}
                          </Badge>
                          {isSaved && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleRemoveFromCampaigns(video.url)
                              }
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(video.url, "_blank")}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {isSaved && savedCampaignNames.length > 0 && (
                        <div className="mb-4 p-2 bg-green-50 rounded-lg">
                          <p className="text-xs text-green-700 font-medium">
                            Saved to: {savedCampaignNames.join(", ")}
                          </p>
                        </div>
                      )}

                      <VideoEmbed
                        url={video.url}
                        platform={
                          video.platform === "youtube-shorts"
                            ? "youtube"
                            : video.platform
                        }
                        className="mb-4"
                      />

                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {video.description || "No description"}
                        </p>

                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline" className="text-xs">
                            👁️ {formatNumber(video.views)}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            ❤️ {formatNumber(video.likes)}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            💬 {formatNumber(video.comments)}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            📤 {formatNumber(video.shares)}
                          </Badge>
                        </div>

                        <div className="flex flex-wrap gap-1">
                          {video.hashtags
                            .slice(0, 3)
                            .map((tag: string, idx: number) => (
                              <Badge
                                key={idx}
                                variant="outline"
                                className="text-xs"
                              >
                                #{tag}
                              </Badge>
                            ))}
                          {video.hashtags.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{video.hashtags.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {videos.length === 0 &&
        !isSearching &&
        Object.keys(groupedVideos).length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              Enter a competitor's handle and search to find their videos, or
              save videos to see them here
            </p>
          </div>
        )}
    </div>
  );
}
